import { jsPDF } from 'jspdf';

interface ReportData {
  success: boolean;
  reportType: string;
  business: {
    name: string;
    id: number;
  };
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  date?: string;
  financialData?: {
    revenue: number;
    expenses: number;
    netIncome: number;
  };
  transactions?: Array<{
    date: string;
    description: string;
    type: string;
    amount: string;
  }>;
  assets?: {
    currentAssets: Array<{ name: string; value: number }>;
    fixedAssets: Array<{ name: string; value: number }>;
    totalCurrentAssets: number;
    totalFixedAssets: number;
    totalAssets: number;
  };
  liabilities?: {
    currentLiabilities: Array<{ name: string; value: number }>;
    longTermLiabilities: Array<{ name: string; value: number }>;
    totalCurrentLiabilities: number;
    totalLongTermLiabilities: number;
    totalLiabilities: number;
  };
  equity?: {
    items: Array<{ name: string; value: number }>;
    totalEquity: number;
  };
  generatedAt: string;
}

export function generatePDFReport(data: ReportData): void {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPosition = 20;

    // Header
    doc.setFontSize(20);
    doc.text(data.business.name, 20, yPosition);
    
    yPosition += 10;
    doc.setFontSize(16);
    doc.text(`${data.reportType.replace('_', ' ').toUpperCase()} REPORT`, 20, yPosition);
    
    yPosition += 10;
    doc.setFontSize(12);
    
    // Handle different date formats for different report types
    if (data.reportType === 'balance_sheet') {
      doc.text(`As of: ${data.date || new Date().toLocaleDateString()}`, 20, yPosition);
    } else if (data.dateRange) {
      doc.text(`Period: ${data.dateRange.startDate} to ${data.dateRange.endDate}`, 20, yPosition);
    }
    
    yPosition += 5;
    doc.text(`Generated: ${new Date(data.generatedAt).toLocaleString()}`, 20, yPosition);

    // Generate Balance Sheet format
    if (data.reportType === 'balance_sheet' && data.assets && data.liabilities && data.equity) {
      yPosition += 20;
      
      // ASSETS SECTION
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('ASSETS', 20, yPosition);
      yPosition += 10;
      
      // Current Assets
      if (data.assets.currentAssets.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Current Assets', 25, yPosition);
        yPosition += 8;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        data.assets.currentAssets.forEach(asset => {
          doc.text(asset.name, 30, yPosition);
          doc.text(`$${asset.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
          yPosition += 6;
        });
        
        // Current Assets Total
        doc.setFont('helvetica', 'bold');
        doc.text('Total Current Assets', 30, yPosition);
        doc.text(`$${data.assets.totalCurrentAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
        yPosition += 10;
      }
      
      // Fixed Assets
      if (data.assets.fixedAssets.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Fixed Assets', 25, yPosition);
        yPosition += 8;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        data.assets.fixedAssets.forEach(asset => {
          doc.text(asset.name, 30, yPosition);
          doc.text(`$${asset.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
          yPosition += 6;
        });
        
        // Fixed Assets Total
        doc.setFont('helvetica', 'bold');
        doc.text('Total Fixed Assets', 30, yPosition);
        doc.text(`$${data.assets.totalFixedAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
        yPosition += 10;
      }
      
      // Total Assets
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL ASSETS', 25, yPosition);
      doc.text(`$${data.assets.totalAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
      yPosition += 20;
      
      // LIABILITIES SECTION
      doc.setFontSize(14);
      doc.text('LIABILITIES', 20, yPosition);
      yPosition += 10;
      
      // Current Liabilities
      if (data.liabilities.currentLiabilities.length > 0) {
        doc.setFontSize(12);
        doc.text('Current Liabilities', 25, yPosition);
        yPosition += 8;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        data.liabilities.currentLiabilities.forEach(liability => {
          doc.text(liability.name, 30, yPosition);
          doc.text(`$${liability.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
          yPosition += 6;
        });
        
        // Current Liabilities Total
        doc.setFont('helvetica', 'bold');
        doc.text('Total Current Liabilities', 30, yPosition);
        doc.text(`$${data.liabilities.totalCurrentLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
        yPosition += 10;
      }
      
      // Long-term Liabilities
      if (data.liabilities.longTermLiabilities.length > 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Long-term Liabilities', 25, yPosition);
        yPosition += 8;
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        data.liabilities.longTermLiabilities.forEach(liability => {
          doc.text(liability.name, 30, yPosition);
          doc.text(`$${liability.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
          yPosition += 6;
        });
        
        // Long-term Liabilities Total
        doc.setFont('helvetica', 'bold');
        doc.text('Total Long-term Liabilities', 30, yPosition);
        doc.text(`$${data.liabilities.totalLongTermLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
        yPosition += 10;
      }
      
      // Total Liabilities
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL LIABILITIES', 25, yPosition);
      doc.text(`$${data.liabilities.totalLiabilities.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
      yPosition += 20;
      
      // EQUITY SECTION
      doc.setFontSize(14);
      doc.text('EQUITY', 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      data.equity.items.forEach(item => {
        doc.text(item.name, 25, yPosition);
        doc.text(`$${item.value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
        yPosition += 6;
      });
      
      // Total Equity
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL EQUITY', 25, yPosition);
      doc.text(`$${data.equity.totalEquity.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
      yPosition += 15;
      
      // Total Liabilities and Equity
      doc.text('TOTAL LIABILITIES & EQUITY', 25, yPosition);
      const totalLiabilitiesAndEquity = data.liabilities.totalLiabilities + data.equity.totalEquity;
      doc.text(`$${totalLiabilitiesAndEquity.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 150, yPosition);
      
    } else {
      // Handle other report types (Profit & Loss, etc.)
      yPosition += 20;
      doc.setFontSize(14);
      doc.text('FINANCIAL SUMMARY (Real ZentroBooks Data)', 20, yPosition);
      
      if (data.financialData) {
        yPosition += 10;
        doc.setFontSize(12);
        doc.text(`Total Revenue: $${data.financialData.revenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 20, yPosition);
        
        yPosition += 8;
        doc.text(`Total Expenses: $${data.financialData.expenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 20, yPosition);
        
        yPosition += 8;
        doc.text(`Net Income: $${data.financialData.netIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 20, yPosition);
      }

      // Transactions for non-Balance Sheet reports
      if (data.transactions && data.transactions.length > 0) {
        yPosition += 20;
        doc.setFontSize(14);
        doc.text(`RECENT TRANSACTIONS (${data.transactions.length} shown)`, 20, yPosition);
        
        yPosition += 10;
        doc.setFontSize(10);
        
        // Table headers
        doc.text('Date', 20, yPosition);
        doc.text('Description', 60, yPosition);
        doc.text('Type', 140, yPosition);
        doc.text('Amount', 170, yPosition);
        
        yPosition += 5;
        
        // Transaction data
        data.transactions.forEach((transaction, index) => {
          if (yPosition > 280) {
            doc.addPage();
            yPosition = 20;
          }
          
          const date = new Date(transaction.date).toLocaleDateString();
          const amount = parseFloat(transaction.amount || '0').toLocaleString('en-US', { minimumFractionDigits: 2 });
          const description = (transaction.description || 'N/A').substring(0, 25);
          
          doc.text(date, 20, yPosition);
          doc.text(description, 60, yPosition);
          doc.text(transaction.type, 140, yPosition);
          doc.text(`$${amount}`, 170, yPosition);
          
          yPosition += 6;
        });
      }
    }

    // Footer
    yPosition += 10;
    doc.setFontSize(8);
    doc.text('Generated by ZentroBooks Financial Reporting System', 20, yPosition);

    // Download the PDF
    const filename = `${data.reportType}_${data.business.name}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF report');
  }
}